import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  barcodeKeys,
  updateProduct,
  updateProductWithVariants,
  getProduct,
  normalizeBarcode,
  deleteProductImage,
  uploadAndAttachProductImage,
  reorderProductImages,
} from '@dukanti/shared';
import type {
  ProductImage,
  ProductImageWithUrl,
  ProductWithImages,
  UpdateProductInput,
  UpdateProductWithVariantsInput,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { productKeys } from './use-products';

/** An entry in the final image order for the update mutation. Existing
 *  images reference their DB id; new files reference their index in
 *  `newFiles`. */
export type UpdateProductImageOrderEntry =
  | { kind: 'existing'; id: string }
  | { kind: 'new'; index: number };

export interface UpdateProductMutationInput {
  productId: string;
  storeId?: string;
  input: UpdateProductInput | UpdateProductWithVariantsInput;
  mode?: UpdateProductWithVariantsInput['mode'];
  options?: UpdateProductWithVariantsInput['options'];
  variants?: UpdateProductWithVariantsInput['variants'];
  /** New files to upload, appended after existing images. */
  newFiles?: File[];
  /** IDs of existing images the user removed. */
  removedImageIds?: string[];
  /** Existing image rows (used to build DELETE calls for removedImageIds).
   *  The caller — the edit form — already has these loaded via
   *  `useProduct`, so we avoid re-fetching. */
  existingImages?: ProductImage[];
  /** Final ordering of images (existing + kept + new). Used to call the
   *  `reorder_product_images` RPC after uploads so the final display
   *  order is exactly what the user arranged. */
  finalOrder?: UpdateProductImageOrderEntry[];
}

export interface UpdateProductResult {
  product: ProductWithImages;
  /** True when at least one new file failed to upload. */
  imagesFailed: boolean;
  failedFileNames: string[];
}

/**
 * Update a product row, reconcile its images, and re-order them.
 *
 * The flow is careful because the DB has a `UNIQUE (product_id,
 * order_index)` constraint:
 *   1. Update product scalar columns (name, description, price, inventory).
 *   2. Delete removed images (DB row first, then best-effort storage).
 *   3. Upload new images at *temporary* high order_indices so they
 *      don't collide with existing ones.
 *   4. Call the `reorder_product_images` RPC with the caller's final
 *      order so every row ends up at its intended 0..N-1 position in
 *      one transaction (design D1 / X1).
 *
 * Every new file upload is wrapped in its own try/catch: one failure
 * does not abort the rest, and the failure is reported via
 * `imagesFailed` + `failedFileNames`.
 */
export function useUpdateProduct(): UseMutationResult<
  UpdateProductResult,
  unknown,
  UpdateProductMutationInput
> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProductResult, unknown, UpdateProductMutationInput>({
    mutationFn: async ({
      productId,
      storeId,
      input,
      mode,
      options,
      variants,
      newFiles = [],
      removedImageIds = [],
      existingImages = [],
      finalOrder = [],
    }) => {
      // Step 1: scalar columns
      const variantInput: UpdateProductWithVariantsInput | null =
        'product' in input && 'mode' in input && 'options' in input && 'variants' in input
          ? (input as UpdateProductWithVariantsInput)
          : null;
      const scalarInput: UpdateProductInput = variantInput
        ? variantInput.product
        : (input as UpdateProductInput);
      const nextMode = variantInput?.mode ?? mode;
      const nextOptions = variantInput?.options ?? options;
      const nextVariants = variantInput?.variants ?? variants;

      if (nextMode && nextOptions && nextVariants) {
        await updateProductWithVariants(supabase, productId, {
          product: scalarInput,
          mode: nextMode,
          options: nextOptions,
          variants: nextVariants,
        });
      } else {
        await updateProduct(supabase, productId, scalarInput);
      }

      // Step 2: remove images the user dropped. Any failure here aborts
      // the whole mutation — losing an image delete is worse than
      // losing a product update, because the merchant would see stale
      // images they thought they deleted. deleteProductImage now uses a
      // transactional RPC (delete_product_image) that atomically removes
      // both the DB row and storage object, so orphan risk is eliminated.
      const removeSet = new Set(removedImageIds);
      for (const img of existingImages) {
        if (!removeSet.has(img.id)) continue;
        try {
          await deleteProductImage(supabase, img);
        } catch (err) {
          console.error('[useUpdateProduct] deleteProductImage failed', err);
          throw err;
        }
      }

      // Step 3: upload new files at temporary high indices so they
      // don't collide with existing rows' order_index. The reorder RPC
      // in Step 4 puts everything back at 0..N-1.
      const existingKept = existingImages.filter((img) => !removeSet.has(img.id));
      const existingKeptCount = existingKept.length;
      const failedFileNames: string[] = [];
      const newImages: Array<ProductImageWithUrl & { newIndex: number }> = [];
      let imagesFailed = false;

      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i]!;
        try {
          // Temporary high order index: well beyond the client-side
          // MAX_PRODUCT_IMAGES cap but within smallint. The reorder
          // RPC Phase 1 shifts to +1000; we pass start=100 so there's
          // no collision with either the final target or Phase 1 offset.
          const tempIndex = 100 + existingKeptCount + i;
          const image = await uploadAndAttachProductImage(supabase, {
            storeId: storeId ?? '',
            productId,
            orderIndex: tempIndex,
            file,
          });
          newImages.push({ ...image, newIndex: i });
        } catch (err) {
          imagesFailed = true;
          failedFileNames.push(file.name);
          console.error('[useUpdateProduct] image upload failed', file.name, err);
          // continue — other files might succeed
        }
      }

      // Step 4: reorder everything to the caller's final order.
      // Map finalOrder entries -> real image ids. New entries whose
      // upload failed are silently dropped from the reorder list.
      const orderedImageIds: string[] = [];
      for (const entry of finalOrder) {
        if (entry.kind === 'existing') {
          if (!removeSet.has(entry.id)) {
            orderedImageIds.push(entry.id);
          }
        } else {
          const match = newImages.find((img) => img.newIndex === entry.index);
          if (match) orderedImageIds.push(match.id);
        }
      }

      if (orderedImageIds.length > 0) {
        await reorderProductImages(supabase, { productId, orderedImageIds });
      }

      // Step 5: re-fetch the product with hydrated images to return
      // the freshest state.
      const fresh = await getProduct(supabase, productId);
      if (!fresh) {
        throw new Error('updated product disappeared');
      }
      return { product: fresh, imagesFailed, failedFileNames };
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        variables.storeId
          ? queryClient.invalidateQueries({ queryKey: productKeys.list(variables.storeId) })
          : Promise.resolve(),
        queryClient.invalidateQueries({
          queryKey: productKeys.detail(variables.productId),
        }),
        variables.storeId
          ? queryClient.invalidateQueries({ queryKey: ['stock-summary', variables.storeId] })
          : Promise.resolve(),
        variables.storeId
          ? queryClient.invalidateQueries({
              queryKey: ['billing', 'entitlement', variables.storeId],
            })
          : Promise.resolve(),
      ]);
      if (!variables.storeId) return;
      const storeId = variables.storeId;
      await Promise.all(
        (
          (variables.variants ??
            ('variants' in variables.input
              ? variables.input.variants
              : [])) as UpdateProductWithVariantsInput['variants']
        )
          .flatMap((variant) => [
            normalizeBarcode(variant.barcode_value ?? ''),
            variant.previous_barcode_normalized ?? '',
          ])
          .filter((barcode) => barcode.length > 0)
          .map((barcode) =>
            queryClient.invalidateQueries({
              queryKey: barcodeKeys.lookup(storeId, barcode),
            }),
          ),
      );
    },
  });
}
